import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSHUCalculation, getFiscalYearOptions } from '../../../services/shu.service';

const CreateSHUCalculation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    fiscal_year: new Date().getFullYear(),
    period_start_date: `${new Date().getFullYear()}-01-01`,
    period_end_date: `${new Date().getFullYear()}-12-31`,
    notes: ''
  });

  const fiscalYearOptions = getFiscalYearOptions(2020);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'fiscal_year' ? parseInt(value) : value
    }));

    // Auto-adjust dates when fiscal year changes
    if (name === 'fiscal_year') {
      setFormData(prev => ({
        ...prev,
        period_start_date: `${value}-01-01`,
        period_end_date: `${value}-12-31`
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fiscal_year || formData.fiscal_year < 2000) {
      setError('Tahun fiskal harus diisi dan minimal 2000');
      return;
    }

    if (!formData.period_start_date || !formData.period_end_date) {
      setError('Periode awal dan akhir harus diisi');
      return;
    }

    if (formData.period_end_date < formData.period_start_date) {
      setError('Tanggal akhir periode harus setelah tanggal awal');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const result = await createSHUCalculation(formData);
      
      // Redirect to detail page
      navigate(`/admin/shu/calculations/${result.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal membuat perhitungan SHU');
      console.error('Error creating SHU calculation:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <button
            onClick={() => navigate('/admin/shu')}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Buat Perhitungan SHU</h1>
            <p className="text-gray-600 mt-1">Buat perhitungan SHU untuk tahun fiskal tertentu</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        {/* Fiscal Year */}
        <div className="mb-6">
          <label htmlFor="fiscal_year" className="block text-sm font-medium text-gray-700 mb-2">
            Tahun Fiskal <span className="text-red-500">*</span>
          </label>
          <select
            id="fiscal_year"
            name="fiscal_year"
            value={formData.fiscal_year}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {fiscalYearOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">
            Pilih tahun untuk perhitungan SHU
          </p>
        </div>

        {/* Period Start Date */}
        <div className="mb-6">
          <label htmlFor="period_start_date" className="block text-sm font-medium text-gray-700 mb-2">
            Tanggal Awal Periode <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="period_start_date"
            name="period_start_date"
            value={formData.period_start_date}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="mt-1 text-sm text-gray-500">
            Tanggal awal periode perhitungan (biasanya 1 Januari)
          </p>
        </div>

        {/* Period End Date */}
        <div className="mb-6">
          <label htmlFor="period_end_date" className="block text-sm font-medium text-gray-700 mb-2">
            Tanggal Akhir Periode <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            id="period_end_date"
            name="period_end_date"
            value={formData.period_end_date}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <p className="mt-1 text-sm text-gray-500">
            Tanggal akhir periode perhitungan (biasanya 31 Desember)
          </p>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Catatan
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            placeholder="Catatan tambahan untuk perhitungan SHU ini..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">Informasi</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Setelah dibuat, Anda perlu melakukan perhitungan untuk mendistribusikan SHU ke anggota</li>
                  <li>Pastikan data keuangan dan data anggota sudah lengkap dan akurat</li>
                  <li>Perhitungan akan menggunakan pengaturan SHU yang sudah dikonfigurasi</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/shu')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Menyimpan...
              </>
            ) : (
              'Buat Perhitungan'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateSHUCalculation;
